import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ASSIGNABLE_MEMBER_ROLES, accountRoles } from '@/lib/account-roles';

const EMAIL_RE = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/;

function memberEditableRole(member) {
  const roles = accountRoles(member);
  if (roles.includes('EDITOR')) {
    return 'EDITOR';
  }
  if (roles.includes('USER')) {
    return 'USER';
  }
  return 'EDITOR';
}

function buildInitialValues(isEdit, member) {
  return isEdit
    ? {
        accountName: member?.accountName ?? '',
        firstName: member?.firstName ?? '',
        lastName: member?.lastName ?? '',
        role: memberEditableRole(member),
      }
    : {
        accountName: '',
        firstName: '',
        lastName: '',
        role: 'EDITOR',
      };
}

export default function TeamMemberModal({
  mode,
  member,
  onSubmit,
  onCancel,
  isSubmitting,
}) {
  const isEdit = mode === 'edit';

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    defaultValues: buildInitialValues(isEdit, member),
    mode: 'onChange',
  });

  useEffect(() => {
    reset(buildInitialValues(isEdit, member));
    trigger();
  }, [isEdit, member, reset, trigger]);

  return (
    <form className="admin-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {isEdit ? <input type="hidden" {...register('accountName')} /> : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="input-group">
          <label htmlFor="member-first">First Name</label>
          <input
            id="member-first"
            type="text"
            placeholder="Jordan"
            {...register('firstName', { required: 'Required' })}
          />
          <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
            {errors.firstName?.message}
          </div>
        </div>
        <div className="input-group">
          <label htmlFor="member-last">Last Name</label>
          <input
            id="member-last"
            type="text"
            placeholder="Smith"
            {...register('lastName', { required: 'Required' })}
          />
          <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
            {errors.lastName?.message}
          </div>
        </div>
      </div>
      {isEdit ? (
        <div className="input-group">
          <label>Email Address</label>
          <p className="m-0 text-sm font-bold text-[#121212]">
            {member?.accountName}
          </p>
        </div>
      ) : (
        <div className="input-group">
          <label htmlFor="member-email">Email Address</label>
          <input
            id="member-email"
            type="email"
            placeholder="jordan@company.com"
            {...register('accountName', {
              required: 'Required',
              validate: (value) =>
                EMAIL_RE.test(value?.trim() ?? '') ||
                'Enter a valid email address',
            })}
          />
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            A welcome email with login instructions will be sent.
          </p>
          <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
            {errors.accountName?.message}
          </div>
        </div>
      )}
      <div className="input-group">
        <label htmlFor="member-role">Role</label>
        <select
          id="member-role"
          {...register('role', { required: 'Required' })}
        >
          {ASSIGNABLE_MEMBER_ROLES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
          {errors.role?.message}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button
          type="button"
          className="inline-flex min-h-11 items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-[var(--bethere-primary)] bg-transparent px-6 font-bold text-[var(--bethere-primary)] transition hover:bg-[var(--bethere-primary)]/5"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !isDirty || !isValid}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] px-6 font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] disabled:opacity-60"
        >
          <FontAwesomeIcon icon={['fas', 'floppy-disk']} />
          {isEdit ? 'Save' : 'Add Member'}
        </button>
      </div>
    </form>
  );
}
